import { useEffect, useMemo, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import SearchIcon from '@ancon/wildcat-ui/shared/icons/i-search.svg'
import { getOutletFormattedAddress } from '@ancon/wildcat-utils/outlet'
import { OutletListItem } from '@ancon/wildcat-types'

import Input from '../../../../app/components/input/Input'
import BodyText from '../../../../app/components/BodyText'
import useAppDispatch from '../../../../../store/hooks/useAppDispatch'
import useAppSelector from '../../../../../store/hooks/useAppSelector'
import { outletsSelectedOutletListItemSelector } from '../../../../outlets/store/outletsSelectors'
import { fetchRestaurantOptions } from '../../../../outlets/store/outletsThunks'
import { OutletsPaginationLimit } from '../../../../outlets/constants'
import useSelectRestaurant from '../../../../outlets/hooks/useSelectRestaurant'
import { setNewOrderDetails } from '../../../store/preOrderSlice'
import PanelOptionList from '../PanelOptionList'

// A guard against loading forever, far above any brand's restaurant count
const MaxRestaurantPages = 20

/**
 * Loads all of the brand's restaurants, page by page, and searches them here
 * by name, city and address. The core search only matches names. Kept out
 * of the outlet list, so the restaurant list behind the panel doesn't change.
 */
export default function RestaurantStep() {
  const { t } = useTranslation('preOrder')
  const dispatch = useAppDispatch()
  const selectRestaurant = useSelectRestaurant()

  const selectedOutlet = useAppSelector(outletsSelectedOutletListItemSelector)

  const [query, setQuery] = useState('')
  const [outlets, setOutlets] = useState<OutletListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isCancelled = false

    async function loadAllOutlets() {
      const limit = OutletsPaginationLimit.Initial

      try {
        for (let page = 0; page < MaxRestaurantPages; page += 1) {
          const offset = page * limit
          const { items, meta } = await dispatch(
            fetchRestaurantOptions({ offset, limit }),
          ).unwrap()

          if (isCancelled) {
            return
          }

          // Each page shows as soon as it arrives
          setOutlets(current => (offset ? [...current, ...items] : items))

          if (!items.length || offset + limit >= meta.count) {
            break
          }
        }
      } catch {
        // The pages loaded so far stay pickable
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    loadAllOutlets()

    return () => {
      isCancelled = true
    }
  }, [dispatch])

  const searchTerm = query.trim()

  const options = useMemo(() => {
    const search = searchTerm.toLocaleLowerCase()

    return outlets
      .map(outlet => ({
        id: outlet.id,
        label: outlet.name,
        hint: getOutletFormattedAddress(outlet.address),
      }))
      .filter(
        ({ label, hint }) =>
          !search || `${label} ${hint}`.toLocaleLowerCase().includes(search),
      )
  }, [outlets, searchTerm])

  function handleSelect(outletId: string) {
    const outlet = outlets.find(({ id }) => id === outletId)

    if (outlet && outlet.id !== selectedOutlet?.id) {
      // The format and time depend on the restaurant
      dispatch(setNewOrderDetails({ orderFormat: null, orderTime: null }))
      selectRestaurant(outlet)
    }
  }

  return (
    <>
      <Input
        leftAccessory={<SearchIcon />}
        placeholder={t('newOrderPanel.restaurantSearch')}
        aria-label={t('newOrderPanel.restaurantSearch')}
        value={query}
        clearable={!!query}
        onChange={event => setQuery(event.target.value)}
        rightAccessoryOnClick={() => setQuery('')}
      />
      {options.length ? (
        <PanelOptionList
          ariaLabel={t('newOrderPanel.steps.restaurant')}
          selectedId={selectedOutlet?.id}
          onSelect={handleSelect}
          options={options}
        />
      ) : (
        !isLoading &&
        !!searchTerm && (
          <BodyText color="body-1">
            {t('newOrderPanel.noRestaurantMatch', { query: searchTerm })}
          </BodyText>
        )
      )}
    </>
  )
}
